// Pure mapping: Scene IR -> Konva node descriptors. Imports NEITHER konva NOR
// react, so it can be unit-tested standalone and reused by BOTH the react-konva
// editor and the vanilla-Konva export page (one source of truth = WYSIWYG parity).

import { ElementTransform, ImageValue, Scene, SceneElement, ScenePaint, SceneShadow, SceneStroke, SceneTransforms, SceneValues } from './types';

export interface KonvaNode {
  konvaType: 'Rect' | 'Text' | 'Image';
  props: Record<string, unknown>;
  /** For images: the URL to load into an HTMLImageElement before drawing. */
  imageSrc?: string;
  /** Source scene element id + editability, for the editor's selection/transform. */
  elementId?: string;
  editable?: boolean;
  /** Input key (for text nodes), so the editor can edit the right value inline. */
  inputKey?: string;
  /** Figma node id. Admin editing records every edit against it, so a node
   *  without one is not admin-selectable (see interactionPolicy). */
  sourceNodeId?: string;
}

/**
 * Konva writes ctx.font UNQUOTED, so a family containing a dot (e.g. the
 * PostScript name "RAG-Marom-2.2-BoldLow") is an invalid CSS font token and the
 * canvas silently falls back to 10px sans-serif. Strip dots to a valid token.
 * The @font-face the renderer registers MUST use this same sanitized name.
 * This is the SINGLE family used for registration + metric measurement — NOT the
 * drawn fontFamily stack (see fontFamilyStack).
 */
export function sanitizeFontFamily(name: string): string {
  return (name || 'sans-serif').replace(/\./g, '-');
}

/** Bundled English/Latin last-resort font. Shipped as a global @font-face (see
 *  fonts.ts) and registered with node-canvas under this exact family in the
 *  export Lambda. The token is unquoted-CSS-safe (letters + hyphen, no dot). */
export const LATIN_FALLBACK_FAMILY = 'FbPracticaPro-Medium';

/** Id prefix of a USER LAYER — an element the end user added on top of the
 *  template (see ./userLayers). It lives here, not in userLayers.ts, because
 *  the occlusion rule below has to recognise one and userLayers.ts imports
 *  from this module (keeping the dependency one-way). */
export const USER_LAYER_ID_PREFIX = 'ul_';

export function isUserLayerId(id: unknown): boolean {
  return typeof id === 'string' && id.startsWith(USER_LAYER_ID_PREFIX);
}

/**
 * The fontFamily STACK written onto the Konva.Text node: the assigned font first,
 * then the bundled Latin last-resort (FbPracticaPro-Medium), then the platform
 * sans-serif. Browsers and node-canvas both do per-glyph fallback across a comma
 * stack, so any glyph the assigned font lacks — or the whole string if that font
 * failed to load — renders in FbPractica before bare sans-serif. Each token is
 * unquoted-CSS-safe (dots stripped). MUST match the lambda's fontFamilyStack.
 */
export function fontFamilyStack(name: string): string {
  const primary = (name || '').replace(/\./g, '-');
  return primary
    ? `${primary}, ${LATIN_FALLBACK_FAMILY}, sans-serif`
    : `${LATIN_FALLBACK_FAMILY}, sans-serif`;
}

/** The preferred (first) family in a sanitized stack. Metric measurement and
 *  font preloading target the assigned font specifically, so they take the head
 *  of the stack rather than the fallback tail (and it's quote-safe for the
 *  single-family ctx.font measurement string). */
export function primaryFontFamily(fontFamily: unknown): string {
  return String(fontFamily || 'sans-serif').split(',')[0].trim() || 'sans-serif';
}

// Figma blend mode -> canvas globalCompositeOperation (Konva applies it per node,
// blending against the backdrop below it on the layer). NORMAL/PASS_THROUGH omit it.
const BLEND_TO_COMPOSITE: Record<string, string> = {
  MULTIPLY: 'multiply', SCREEN: 'screen', OVERLAY: 'overlay',
  DARKEN: 'darken', LIGHTEN: 'lighten', COLOR_DODGE: 'color-dodge', COLOR_BURN: 'color-burn',
  HARD_LIGHT: 'hard-light', SOFT_LIGHT: 'soft-light', DIFFERENCE: 'difference', EXCLUSION: 'exclusion',
  HUE: 'hue', SATURATION: 'saturation', COLOR: 'color', LUMINOSITY: 'luminosity',
};

export function blendModeToComposite(mode: string | undefined): string | undefined {
  return mode ? BLEND_TO_COMPOSITE[mode] : undefined;
}

/** Map a structured paint to Konva fill props for a node sized w x h. */
export function paintToKonvaFill(paint: ScenePaint | null | undefined, w: number, h: number): Record<string, unknown> {
  if (!paint) return {};
  if (paint.type === 'solid') return { fill: paint.color };

  // Konva colorStops format is a flat [pos, color, pos, color, ...] array.
  const colorStops = (paint.stops || []).flatMap((s) => [s.position, s.color]);
  const handles = paint.handles || [];

  if (paint.type === 'linear') {
    const [sx, sy] = handles[0] || [0, 0];
    const [ex, ey] = handles[1] || [0, 1];
    return {
      fillLinearGradientStartPoint: { x: sx * w, y: sy * h },
      fillLinearGradientEndPoint: { x: ex * w, y: ey * h },
      fillLinearGradientColorStops: colorStops,
    };
  }
  // radial / angular / diamond -> approximated as a radial gradient (warned at import)
  const [cx, cy] = handles[0] || [0.5, 0.5];
  const [ex, ey] = handles[1] || [1, 0.5];
  const radius = Math.hypot((ex - cx) * w, (ey - cy) * h);
  return {
    fillRadialGradientStartPoint: { x: cx * w, y: cy * h },
    fillRadialGradientEndPoint: { x: cx * w, y: cy * h },
    fillRadialGradientStartRadius: 0,
    fillRadialGradientEndRadius: radius,
    fillRadialGradientColorStops: colorStops,
  };
}

function strokeProps(stroke: SceneStroke | undefined): Record<string, unknown> {
  if (!stroke) return {};
  // Konva strokes are a single colour; gradient strokes fall back to the first stop.
  const color = stroke.paint.type === 'solid' ? stroke.paint.color : stroke.paint.stops?.[0]?.color;
  const props: Record<string, unknown> = { stroke: color, strokeWidth: stroke.weight };
  if (stroke.dashPattern && stroke.dashPattern.length) props.dash = stroke.dashPattern;
  return props;
}

/**
 * The one shadow Konva renders ON the node itself (it allows only one). It must
 * be the SHARPEST (smallest blur): a tight shadow only reads when cast by the
 * real glyph on top — as a behind-the-glyph clone it's hidden by the node and
 * washed out by a softer shadow drawn over it. Soft/wide shadows survive fine as
 * clones (they spread well beyond the glyph), so they're the ones relegated to
 * clones. Picking the largest blur instead made the crisp edge shadow disappear
 * (PSD text effects = a soft halo + sharp edge layered together). MUST match the
 * lambda's pickPrimaryShadow (sceneToKonva.js).
 */
function pickPrimaryShadow(shadows: SceneShadow[]): SceneShadow {
  return shadows.reduce((a, b) => (b.blur < a.blur ? b : a), shadows[0]);
}

function shadowProps(shadows: SceneShadow[] | undefined): Record<string, unknown> {
  if (!shadows || !shadows.length) return {};
  // Konva renders one shadow per node; render the sharpest here (see pickPrimaryShadow).
  // Additional shadows are stacked as clone nodes in sceneToKonva (see below).
  const s = pickPrimaryShadow(shadows);
  return {
    shadowColor: s.color,
    shadowBlur: s.blur,
    shadowOffsetX: s.offsetX,
    shadowOffsetY: s.offsetY,
    // Figma drop-shadows are cast by the whole layer (fill + stroke). Text here
    // has a thick OUTSIDE stroke, so a fill-only shadow stays hidden behind the
    // stroke and reads as almost no shadow. Include the stroke so the shadow
    // actually shows (no effect on unstroked nodes).
    shadowForStrokeEnabled: true,
  };
}

/**
 * Text stroke props honouring Figma stroke ALIGNMENT. Konva strokes text centered
 * on the glyph path and (by default) draws the stroke ON TOP of the fill, so a
 * Figma "OUTSIDE" outline ends up eating into the letters (looks thin + the border
 * sits inline). Emulate OUTSIDE by drawing the fill AFTER the stroke
 * (`fillAfterStrokeEnabled`) and DOUBLING the width, so the fill covers the inner
 * half and only ~`weight` of stroke shows outside the glyph.
 */
function textStrokeProps(stroke: SceneStroke | undefined): Record<string, unknown> {
  if (!stroke) return {};
  const color = stroke.paint.type === 'solid' ? stroke.paint.color : stroke.paint.stops?.[0]?.color;
  const props: Record<string, unknown> = { stroke: color };
  if (stroke.dashPattern && stroke.dashPattern.length) props.dash = stroke.dashPattern;
  if (stroke.align === 'OUTSIDE') {
    props.strokeWidth = stroke.weight * 2;
    props.fillAfterStrokeEnabled = true; // fill on top -> only the outer half shows
  } else {
    // CENTER (and INSIDE, approximated): Konva's native centered stroke.
    props.strokeWidth = stroke.weight;
  }
  return props;
}

function cornerRadius(radius?: number, cornerRadii?: number[]): number | number[] | undefined {
  if (cornerRadii && cornerRadii.length === 4) return cornerRadii; // [tl, tr, br, bl]
  return radius || undefined;
}

function imageValue(values: SceneValues, key?: string): ImageValue | undefined {
  if (!key) return undefined;
  const v = values[key];
  return v && typeof v === 'object' ? (v as ImageValue) : undefined;
}

function textValue(values: SceneValues, key: string | undefined, fallback: string): string {
  if (!key) return fallback;
  const v = values[key];
  return typeof v === 'string' ? v : fallback;
}

// Hebrew (U+0590–05FF), Arabic + supplements/extended, and Arabic Presentation
// Forms. MUST match the lambda's RTL_RE (sceneToKonva.js).
const RTL_RE = /[֐-׿؀-ۿݐ-ݿࢠ-ࣿיִ-﷿ﹰ-﻿]/;

/**
 * First-strong-character paragraph direction (UAX#9 P2/P3) — mirrors `dir="auto"`.
 * Returns 'rtl' | 'ltr' | null (no strong directional char; caller leaves default).
 * Konva.Text defaults to direction 'inherit', and when a stroked+shadowed node is
 * drawn through Konva's offscreen BUFFER canvas (shadowForStrokeEnabled) that
 * buffer's context is LTR — so RTL text reverses unless we set direction:'rtl'
 * explicitly. MUST stay in lockstep with the lambda's paragraphDirection.
 */
function paragraphDirection(s: string): 'rtl' | 'ltr' | null {
  if (typeof s !== 'string') return null;
  for (const ch of s) {
    if (RTL_RE.test(ch)) return 'rtl';
    if (/\p{L}/u.test(ch)) return 'ltr';
  }
  return null;
}

/** Map one scene element to a Konva node descriptor (or null if unrenderable).
 *  `transform` (from inline editing) overrides the element's geometry.
 *  `opts.preferThumbnail` switches the image-resolution fallback chain (see
 *  the image branch below) to use the asset's thumbnail when present. */
export function elementToKonva(el: SceneElement, values: SceneValues, transform?: ElementTransform, opts: SceneToKonvaOptions = {}): KonvaNode | null {
  const x = transform?.x ?? el.x;
  const y = transform?.y ?? el.y;
  const w = transform?.w ?? el.w;
  const h = transform?.h ?? el.h;
  const rotation = transform?.rotation ?? el.rotation ?? 0;
  const gco = blendModeToComposite(el.blendMode);
  const base = {
    id: el.id,
    name: el.inputKey || el.name,
    x,
    y,
    rotation,
    opacity: el.opacity ?? 1,
    ...(gco ? { globalCompositeOperation: gco } : {}),
    ...shadowProps(el.shadows),
  };
  const meta = { elementId: el.id, editable: !!el.editable, inputKey: el.inputKey, sourceNodeId: el.sourceNodeId };

  if (el.type === 'text' && el.text) {
    const t = el.text;
    // Font-size override (form panel's font +/-). Line height is a RATIO in
    // Konva, so it scales with the size for free; letter spacing is in px,
    // so scale it by the same factor to keep the tracking proportional.
    const fontSize = transform?.fontSize && transform.fontSize > 0 ? transform.fontSize : t.fontSize;
    const fontScale = t.fontSize > 0 ? fontSize / t.fontSize : 1;
    const lineHeight = t.lineHeightPx && t.fontSize ? t.lineHeightPx / t.fontSize : 1;
    const fill = paintToKonvaFill(t.fill, w, h);
    const resolvedText = textValue(values, el.inputKey, t.content);
    return {
      ...meta,
      konvaType: 'Text',
      props: {
        ...base,
        width: w,
        // Box height for the inline-edit overlay only (underscore-prefixed, like
        // _scaleMode). NOT passed as Konva `height` — that would activate
        // verticalAlign and shift the committed render / break export parity.
        _boxH: h,
        text: resolvedText,
        fontFamily: fontFamilyStack(t.fontPostScriptName),
        fontSize,
        align: t.align,
        verticalAlign: t.verticalAlign === 'middle' ? 'middle' : t.verticalAlign === 'bottom' ? 'bottom' : 'top',
        lineHeight,
        letterSpacing: (t.letterSpacing || 0) * fontScale,
        wrap: 'word',
        // Figma trimmed-box marker — resolved to a y-shift at draw time
        // (needs the loaded font); never a Konva attr.
        ...(t.leadingTrim === 'CAP_HEIGHT' ? { _leadingTrim: 'CAP_HEIGHT' } : {}),
        // Base bidi direction for RTL (Hebrew/Arabic). Without this, Konva's
        // offscreen buffer-canvas path (stroked+shadowed text) renders RTL reversed.
        ...(paragraphDirection(resolvedText) === 'rtl' ? { direction: 'rtl' } : {}),
        ...fill,
        ...textStrokeProps(t.stroke),
      },
    };
  }

  if (el.type === 'image' && el.image) {
    const override = imageValue(values, el.inputKey);
    // Resolution fallback chain for an image element. Two concerns:
    //   1. Video-as-image: an override whose `highRes` is .mp4/.webm/etc.
    //      crashes HTMLImageElement (broken state). Use the override's
    //      `thumbnail` (poster frame) instead.
    //   2. Low-quality preview (opts.preferThumbnail, set by the editor):
    //      use the override's thumbnail when available so the browser
    //      doesn't pull MB-scale assets. Server-side export leaves this off
    //      and gets the full-resolution highRes.
    const isVideoUrl = (u: unknown): boolean =>
      typeof u === 'string' && /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u);
    let overrideSrc: string | undefined;
    if (override) {
      if (isVideoUrl(override.highRes)) {
        overrideSrc = override.thumbnail || override.highRes;
      } else if (opts.preferThumbnail) {
        overrideSrc = override.thumbnail || override.highRes;
      } else {
        overrideSrc = override.highRes;
      }
    }
    const src = overrideSrc || el.image.src;
    // USER-REPLACED image: contain it inside the box (transparent margins)
    // instead of cover-cropping, so nothing the user chose is cut. Never for a
    // shape-masked collage cell — those keep cover + pan/zoom, which is the
    // whole point of the framing gesture (decision #4, docs/user-layers-plan.md).
    const userFit = !el.image.shapeMask && el.inputKey ? opts.imageFit?.[el.inputKey] : undefined;
    return {
      ...meta,
      konvaType: 'Image',
      imageSrc: src,
      props: {
        ...base,
        width: w,
        height: h,
        // A shape mask replaces the box outline with the mask shape, so a box
        // cornerRadius/stroke (Konva would trace them on the RECT, not the mask)
        // must NOT be applied — that's a stray rectangle border around a circle.
        ...(el.image.shapeMask ? {} : {
          cornerRadius: cornerRadius(el.image.radius, el.image.cornerRadii),
          ...strokeProps(el.image.stroke),
        }),
        // `fit` is resolved when the HTMLImageElement is known (see coverCrop).
        _scaleMode: el.image.scaleMode,
        // Overrides _scaleMode at draw time (bakeContain). Underscore-prefixed:
        // resolved at image-load time, never a Konva attr.
        ...(userFit === 'contain' ? { _fit: 'contain' } : {}),
        // Linear alpha fade over the element box (PSD gradient-mask import).
        // Underscore-prefixed: resolved at image-load time, never a Konva attr.
        ...(el.image.fadeMask ? { _fadeMask: el.image.fadeMask } : {}),
        // Vector clip mask (ellipse/star/path crop). Resolved (baked) at
        // image-load time too; never a Konva attr.
        ...(el.image.shapeMask ? { _shapeMask: el.image.shapeMask } : {}),
        // Cover-pan offset for a clipped image (collage framing): the editor's
        // ElementTransform.panX/panY overrides the image's default `pan`.
        // Pan/zoom only applies to cover-FILL masks; FIT ignores it in the bake,
        // so don't advertise it (would make a FIT mask draggable-but-frozen).
        ...(el.image.shapeMask && el.image.scaleMode !== 'FIT'
          ? { _imagePan: {
              x: transform?.panX ?? el.image.pan?.x ?? 0,
              y: transform?.panY ?? el.image.pan?.y ?? 0,
              zoom: transform?.panZoom ?? el.image.pan?.zoom ?? 1,
            } }
          : {}),
      },
    };
  }

  if (el.type === 'rect' && el.rect) {
    const fill = paintToKonvaFill(el.rect.fills?.[0], w, h);
    return {
      ...meta,
      konvaType: 'Rect',
      props: {
        ...base,
        width: w,
        height: h,
        cornerRadius: cornerRadius(el.rect.radius, el.rect.cornerRadii),
        ...fill,
        ...strokeProps(el.rect.stroke),
      },
    };
  }

  return null;
}

/**
 * object-fit:contain box for `scaleMode:'FIT'`: the image scaled to fit inside
 * the box, centered (letterboxed). Returns the Konva.Image x/y/width/height.
 */
export function containFit(
  imgW: number,
  imgH: number,
  boxW: number,
  boxH: number,
  boxX: number,
  boxY: number,
): { x: number; y: number; width: number; height: number } {
  if (!imgW || !imgH) return { x: boxX, y: boxY, width: boxW, height: boxH };
  const scale = Math.min(boxW / imgW, boxH / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return { x: boxX + (boxW - w) / 2, y: boxY + (boxH - h) / 2, width: w, height: h };
}

/**
 * Crop rectangle for object-fit:cover of `img` into a `boxW x boxH` Konva.Image.
 * (Konva has no native cover; you compute a crop on the source image.)
 */
export function coverCrop(
  imgW: number,
  imgH: number,
  boxW: number,
  boxH: number,
): { x: number; y: number; width: number; height: number } {
  if (!imgW || !imgH) return { x: 0, y: 0, width: imgW, height: imgH };
  const scale = Math.max(boxW / imgW, boxH / imgH);
  const cw = boxW / scale;
  const ch = boxH / scale;
  return { x: (imgW - cw) / 2, y: (imgH - ch) / 2, width: cw, height: ch };
}

/**
 * The source rectangle to draw into a cover-FILL box, given a pan + zoom
 * (collage framing / drag-to-reposition). Starts from the centered cover crop,
 * ZOOMS in by `pan.zoom` (≥ 1 — a smaller, magnified source region), then PANS
 * within the resulting overflow (x/y ∈ [-1, 1], 0 = centered, no-op on a
 * non-overflowing axis). Returns {x, y, width, height} in source px. Pure +
 * shared so editor, export, and the drag math agree. `crop` is coverCrop()'s.
 */
/** Magnification ceiling — bounds pathological / exact-crop zoom so the source
 *  region never degenerates to sub-pixel. MUST match the lambda port. */
export const MAX_PAN_ZOOM = 16;

/** Clamp to [lo, hi], substituting `dflt` for NaN/±Infinity. `??` alone lets
 *  NaN through, and the two render ports diverged on it (`??` kept NaN → blank;
 *  `||` coerced to 0); this makes both finite + identical. */
export function clampFinite(v: number | undefined | null, lo: number, hi: number, dflt: number): number {
  return Number.isFinite(v as number) ? Math.max(lo, Math.min(hi, v as number)) : dflt;
}

/** Per-click zoom factor for the editor's +/- collage-cell zoom steppers. */
export const ZOOM_STEP = 1.2;

/** Step a collage cell's pan-zoom one notch for the +/- buttons. Multiplicative
 *  so each click feels even, clamped to [1, MAX_PAN_ZOOM] (1 = the cover-FILL
 *  baseline — no magnification, the image exactly fills the cell). */
export function stepZoom(current: number | undefined | null, direction: 1 | -1): number {
  const base = clampFinite(current, 1, MAX_PAN_ZOOM, 1);
  const next = direction > 0 ? base * ZOOM_STEP : base / ZOOM_STEP;
  return clampFinite(Math.round(next * 1000) / 1000, 1, MAX_PAN_ZOOM, 1);
}

/**
 * True for a full-board image stacked ABOVE smaller image content — e.g. a
 * collage frame/foreground overlay sitting over the photo cells. Konva hit-tests
 * an image by its whole bounding rectangle (transparent pixels included), so such
 * an overlay swallows every click meant for the cells beneath it. The editor
 * marks these `listening:false` so clicks fall through (they stay selectable from
 * the form panel). `below` is the set of nodes rendered under `node` (lower z).
 */
export function isOccludingOverlay(node: KonvaNode, below: KonvaNode[], sceneW: number, sceneH: number): boolean {
  if (node.konvaType !== 'Image') return false;
  // A USER LAYER is never a decorative frame: the user put it there and must be
  // able to select it, however big it is — and a small image layer under a
  // template backdrop must not turn that backdrop inert either. Exempt on both
  // sides of the test.
  if (isUserLayerId(node.elementId)) return false;
  const w = node.props.width as number;
  const h = node.props.height as number;
  const fullBoard = Math.abs(w - sceneW) < 2 && Math.abs(h - sceneH) < 2;
  if (!fullBoard) return false;
  // Only an overlay if it actually covers smaller image content below it; a
  // bottom-of-stack full-board backdrop has nothing to occlude and stays clickable.
  return below.some((n) => n.konvaType === 'Image' && !isUserLayerId(n.elementId) && (n.props.width as number) < sceneW - 2);
}

export function panZoomSource(
  crop: { x: number; y: number; width: number; height: number },
  srcW: number,
  srcH: number,
  pan?: { x: number; y: number; zoom?: number } | null,
): { x: number; y: number; width: number; height: number } {
  const z = clampFinite(pan?.zoom, 1, MAX_PAN_ZOOM, 1);
  const w = Math.max(1, crop.width / z);
  const h = Math.max(1, crop.height / z);
  const px = clampFinite(pan?.x, -1, 1, 0);
  const py = clampFinite(pan?.y, -1, 1, 0);
  return {
    x: ((srcW - w) / 2) * (1 + px),
    y: ((srcH - h) / 2) * (1 + py),
    width: w,
    height: h,
  };
}

/**
 * Editor drag-to-pan: convert a drag (dxBox/dyBox, in ELEMENT-box px) from the
 * pan that was in effect at drag-start into the new pan. Dragging the image
 * content right reveals more of its left → pan decreases (sign flipped). The
 * full display-overflow drag spans the whole [-1, 1] range; no-op on a
 * non-overflowing axis. Pure + shared so the editor and the render math agree.
 */
export function panFromDrag(
  startPan: { x: number; y: number },
  dxBox: number,
  dyBox: number,
  srcW: number,
  srcH: number,
  boxW: number,
  boxH: number,
  zoom = 1,
): { x: number; y: number } {
  const c = coverCrop(srcW, srcH, boxW, boxH);
  const z = clampFinite(zoom, 1, MAX_PAN_ZOOM, 1);
  const w = c.width / z;
  const h = c.height / z;
  const ovX = srcW - w;
  const ovY = srcH - h;
  const dpx = ovX > 0 ? (-2 * dxBox * w) / (boxW * ovX) : 0;
  const dpy = ovY > 0 ? (-2 * dyBox * h) / (boxH * ovY) : 0;
  return {
    x: Math.max(-1, Math.min(1, startPan.x + dpx)),
    y: Math.max(-1, Math.min(1, startPan.y + dpy)),
  };
}

/**
 * Bake a SceneFadeMask into a bitmap: draw the (optionally cropped) source
 * onto a canvas, then erase alpha with a linear gradient composited
 * 'destination-in'. Returns a canvas the caller hands to Konva.Image INSTEAD
 * of the source (when `crop` was given, the canvas IS the cropped region —
 * don't also set Konva's crop). Canvas size = the visible region at source
 * resolution, so no quality is lost. MUST stay in lockstep with the lambda's
 * fadeImage (renderSceneNode.js) — editor preview === export.
 *
 * Returns null when a 2d context is unavailable (caller falls back to the
 * unfaded source).
 */
export function applyFadeMask(
  img: CanvasImageSource,
  srcW: number,
  srcH: number,
  crop: { x: number; y: number; width: number; height: number } | null,
  fade: { direction: 'down' | 'up' | 'left' | 'right'; start: number; end: number },
): HTMLCanvasElement | null {
  const sx = crop ? crop.x : 0;
  const sy = crop ? crop.y : 0;
  const sw = crop ? crop.width : srcW;
  const sh = crop ? crop.height : srcH;
  const cw = Math.max(1, Math.round(sw));
  const ch = Math.max(1, Math.round(sh));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = fadeGradient(ctx, cw, ch, fade);
  ctx.fillRect(0, 0, cw, ch);
  return canvas;
}

/** Linear alpha gradient for applyFadeMask: opaque before `start`, transparent
 *  after `end` (fractions of the box along `direction`). Canvas gradients clamp
 *  beyond the outermost stops, so two stops cover the full ramp. */
function fadeGradient(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  fade: { direction: 'down' | 'up' | 'left' | 'right'; start: number; end: number },
): CanvasGradient {
  const pts: [number, number, number, number] =
    fade.direction === 'up' ? [0, h, 0, 0]
    : fade.direction === 'left' ? [w, 0, 0, 0]
    : fade.direction === 'right' ? [0, 0, w, 0]
    : [0, 0, 0, h];
  const g = ctx.createLinearGradient(...pts);
  const start = Math.min(1, Math.max(0, Number(fade.start) || 0));
  const end = Math.min(1, Math.max(start + 0.0001, Number(fade.end) || 1));
  g.addColorStop(start, 'rgba(0,0,0,1)');
  g.addColorStop(end, 'rgba(0,0,0,0)');
  return g;
}

/** Minimal 2d-path sink — the subset of CanvasRenderingContext2D that
 *  traceMaskPath drives. Lets the tracer be unit-tested with a recording stub. */
export interface MaskPathSink {
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  bezierCurveTo(c1x: number, c1y: number, c2x: number, c2y: number, x: number, y: number): void;
  quadraticCurveTo(cx: number, cy: number, x: number, y: number): void;
  closePath(): void;
}

/**
 * Trace a clip-mask path onto a 2d context. The path is in element-box
 * fractions (0..1) with ABSOLUTE SVG commands M/L/C/Q/Z (as the extractor
 * emits); each coordinate is scaled by (sx, sy). Pure path commands only — NO
 * Path2D (it's unavailable in node-canvas). MUST stay in lockstep with the
 * lambda's traceMaskPath (sceneToKonva.js) so editor === export.
 */
export function traceMaskPath(ctx: MaskPathSink, path: string, sx: number, sy: number): void {
  const toks = path.match(/[MLCQZ]|-?\d*\.?\d+(?:e-?\d+)?/gi);
  if (!toks) return;
  let i = 0;
  const X = () => parseFloat(toks[i++]) * sx;
  const Y = () => parseFloat(toks[i++]) * sy;
  while (i < toks.length) {
    const c = toks[i++].toUpperCase();
    if (c === 'M') ctx.moveTo(X(), Y());
    else if (c === 'L') ctx.lineTo(X(), Y());
    else if (c === 'C') ctx.bezierCurveTo(X(), Y(), X(), Y(), X(), Y());
    else if (c === 'Q') ctx.quadraticCurveTo(X(), Y(), X(), Y());
    else if (c === 'Z') ctx.closePath();
  }
}

/** Per-axis cap on a baked mask canvas (matches the lambda's SCENE_MAX_DIM
 *  default) so a pathological element box can't pin a huge native allocation. */
export const MAX_MASK_DIM = 8192;

function clampMaskDim(v: number): number {
  return Math.max(1, Math.min(MAX_MASK_DIM, Math.round(v)));
}

/**
 * Bake a vector clip mask into a bitmap that fills the element box: draw the
 * image positioned per `scaleMode` (FIT = contain/letterbox; else cover), then
 * erase alpha outside the mask path with `destination-in`. The returned canvas
 * is handed to Konva.Image at the element box (x,y,w,h) with NO crop and NO
 * containFit — the bitmap already holds the positioned, masked image. FILL
 * bakes at the cropped SOURCE resolution (a large photo keeps its detail, like
 * applyFadeMask); FIT at box resolution (the image is downscaled to fit anyway).
 * MUST stay in lockstep with the lambda's shapeMaskImage (renderSceneNode.js).
 * Returns null when a 2d context is unavailable (caller falls back to the image).
 */
export function applyShapeMask(
  img: CanvasImageSource,
  srcW: number,
  srcH: number,
  boxW: number,
  boxH: number,
  scaleMode: string | undefined,
  mask: { path: string; fillRule?: 'nonzero' | 'evenodd' },
  pan?: { x: number; y: number; zoom?: number } | null,
): HTMLCanvasElement | null {
  let cw: number;
  let ch: number;
  let draw: (ctx: CanvasRenderingContext2D) => void;
  if (scaleMode === 'FIT') {
    cw = clampMaskDim(boxW);
    ch = clampMaskDim(boxH);
    const f = containFit(srcW, srcH, cw, ch, 0, 0);
    draw = (ctx) => ctx.drawImage(img, f.x, f.y, f.width, f.height);
  } else {
    const c = coverCrop(srcW, srcH, boxW, boxH);
    // Pan+zoom the cover crop within the source (collage framing). The canvas is
    // the cover region's size; a zoomed (smaller) source region drawn into it
    // magnifies, and pan slides it within the overflow.
    const s = panZoomSource(c, srcW, srcH, pan);
    cw = clampMaskDim(c.width);
    ch = clampMaskDim(c.height);
    draw = (ctx) => ctx.drawImage(img, s.x, s.y, s.width, s.height, 0, 0, cw, ch);
  }
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  draw(ctx);
  // The canvas represents the element box, so the box-fraction path scales by
  // the canvas dims regardless of whether that's box or source resolution.
  ctx.globalCompositeOperation = 'destination-in';
  ctx.beginPath();
  traceMaskPath(ctx, mask.path, cw, ch);
  ctx.fill(mask.fillRule === 'evenodd' ? 'evenodd' : 'nonzero');
  return canvas;
}

/**
 * Bake an object-fit:contain draw into a BOX-SIZED transparent bitmap: the
 * image scaled to fit inside the box, centered, with transparent margins the
 * template shows through. The result is handed to Konva.Image at the element
 * box (x,y,w,h) with NO crop and NO geometry change — which is the point: the
 * user drags and resizes the BOX, so the node must stay the box or a persisted
 * `sceneTransforms` entry would compound with the contain rect on re-render.
 * Modelled on applyShapeMask's FIT branch (box resolution — the image is being
 * downscaled to fit anyway). MUST stay in lockstep with the lambda's
 * bakeContain (renderSceneNode.js). Returns null when a 2d context is
 * unavailable (caller falls back to the uncontained image).
 */
export function bakeContain(
  img: CanvasImageSource,
  srcW: number,
  srcH: number,
  boxW: number,
  boxH: number,
): HTMLCanvasElement | null {
  const cw = clampMaskDim(boxW);
  const ch = clampMaskDim(boxH);
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const f = containFit(srcW, srcH, cw, ch, 0, 0);
  ctx.drawImage(img, f.x, f.y, f.width, f.height);
  return canvas;
}

let trimMeasureCtx: CanvasRenderingContext2D | null = null;

/**
 * Vertical shift (px, positive = draw higher) that makes a Figma
 * leadingTrim=CAP_HEIGHT text node's glyph cap-top land exactly at the
 * element's y. Figma's trimmed bounding box top IS the cap-top; Konva knows
 * no leadingTrim — it anchors each line's glyph 'middle' baseline at the
 * line-box center (fontSize*lineHeight/2 below the node top), which puts
 * the cap visibly LOWER (~45px on a 217px/260px headline). Measure the real
 * ink ascent of the first line with the loaded font and shift by the
 * difference. Call only after the font is available (fontsReady / fonts
 * registered) — Konva's own metrics carry the same dependency. MUST stay in
 * lockstep with the lambda's leadingTrimYOffset (renderSceneNode.js).
 */
export function leadingTrimYOffset(props: Record<string, unknown>): number {
  if (props._leadingTrim !== 'CAP_HEIGHT') return 0;
  const fontSize = typeof props.fontSize === 'number' ? props.fontSize : 0;
  const lineHeight = typeof props.lineHeight === 'number' && props.lineHeight > 0 ? props.lineHeight : 1;
  const firstLine = String(props.text ?? '').split('\n')[0];
  if (!fontSize || !firstLine) return 0;
  if (!trimMeasureCtx) trimMeasureCtx = document.createElement('canvas').getContext('2d');
  if (!trimMeasureCtx) return 0;
  // Measure with the ASSIGNED (primary) family only — props.fontFamily is a comma
  // stack (assigned, FbPracticaPro-Medium, sans-serif); a stack inside ctx.font's
  // single quoted family would be an invalid token and fall back to default
  // metrics. The assigned font is what renders the first line's glyphs.
  trimMeasureCtx.font = `${fontSize}px "${primaryFontFamily(props.fontFamily)}"`;
  trimMeasureCtx.textBaseline = 'middle';
  const m = trimMeasureCtx.measureText(firstLine);
  const ascent = typeof m.actualBoundingBoxAscent === 'number' ? m.actualBoundingBoxAscent : fontSize * 0.5;
  return (fontSize * lineHeight) / 2 - ascent;
}

/**
 * Apply the leading-trim vertical shift to a Konva Text's props for rendering.
 *
 * Two coupled corrections, both keyed off the same `trimOffset`:
 *  1. The node is moved UP by `trimOffset` so the glyph cap-top lands at the
 *     element box top (the original purpose of leadingTrim).
 *  2. A text FILL/STROKE gradient is mapped (in `paintToKonvaFill`) to the
 *     element box [0..h] at the node origin — but Konva draws the glyph cap
 *     `trimOffset` BELOW that origin (centered in the lineHeightPx slot). So the
 *     gradient band must be moved DOWN by `trimOffset` to stay on the glyphs;
 *     otherwise the ink falls outside the band and renders as the clamped end
 *     stop (a flat colour). Without this, gradient-filled trimmed text — e.g.
 *     PSD imports whose box h << fontSize — renders flat. MUST stay in lockstep
 *     with the lambda's applyLeadingTrimShift (renderSceneNode.js).
 */
export function applyLeadingTrimShift(props: Record<string, unknown>, trimOffset: number): Record<string, unknown> {
  if (!trimOffset) return props;
  const out: Record<string, unknown> = { ...props, y: (props.y as number) - trimOffset };
  for (const key of ['fillLinearGradientStartPoint', 'fillLinearGradientEndPoint', 'fillRadialGradientStartPoint', 'fillRadialGradientEndPoint']) {
    const pt = props[key] as { x: number; y: number } | undefined;
    if (pt && typeof pt.y === 'number') out[key] = { x: pt.x, y: pt.y + trimOffset };
  }
  return out;
}

/** CSS for the HTML <textarea> overlay used to edit a text node inline. Reads the
 *  text node's RESOLVED props (post-transform) + the stage display `scale`, so the
 *  overlay sits exactly over the canvas text. v1 assumes no rotation (the importer
 *  drops it; rotated elements skip inline-edit). Pure -> unit-tested. */
export function textOverlayStyle(
  props: Record<string, unknown>,
  scale: number,
): { left: number; top: number; width: number; height: number; fontSize: number; lineHeight: number; letterSpacing: number; textAlign: string; fontFamily: string; color: string } {
  const num = (v: unknown, d = 0) => (typeof v === 'number' ? v : d);
  const fontSize = num(props.fontSize, 16);
  const lineHeight = num(props.lineHeight, 1) || 1;
  // The textarea must MIRROR Konva's text line-box, not the design cap-height box
  // (_boxH). _boxH is the trimmed cap height — far shorter than the font — so a
  // textarea that tall clips/hides its own text (the glyph is centred in the
  // fontSize*lineHeight line-box, below the _boxH slice). Use the full line-box
  // height (and the caller passes the trim-shifted top, = Konva's node y) so the
  // overlay text lands exactly on the rendered glyph. Take max with _boxH so a
  // genuinely multi-line / tall box keeps room for all its lines.
  return {
    left: num(props.x) * scale,
    top: num(props.y) * scale,
    width: num(props.width) * scale,
    height: Math.max(fontSize * lineHeight, num(props._boxH)) * scale,
    fontSize: fontSize * scale,
    lineHeight,
    letterSpacing: num(props.letterSpacing) * scale,
    textAlign: typeof props.align === 'string' ? props.align : 'right',
    fontFamily: typeof props.fontFamily === 'string' ? props.fontFamily : 'sans-serif',
    // Gradient-filled text has no string `fill`; the overlay (editor-only) uses a
    // solid colour — the committed value still renders via the gradient on export.
    color: typeof props.fill === 'string' ? props.fill : '#000',
  };
}

/** Enforce a text input's constraints as the user types/pastes in the overlay:
 *  single-line inputs collapse newlines; maxChars truncates. Mirrors the legacy
 *  contentEditable maxChars/inputType handling. Pure -> unit-tested. */
export function sanitizeTextInput(value: string, opts: { maxChars?: number; multiline?: boolean }): string {
  let v = value;
  if (!opts.multiline) v = v.replace(/[\r\n]+/g, ' ');
  if (opts.maxChars && opts.maxChars > 0 && v.length > opts.maxChars) v = v.slice(0, opts.maxChars);
  return v;
}

/** Background paint -> a full-bleed Konva.Rect descriptor (or null). */
export function backgroundNode(scene: Scene): KonvaNode | null {
  if (!scene.background) return null;
  return {
    konvaType: 'Rect',
    props: {
      id: '__bg__',
      x: 0,
      y: 0,
      width: scene.width,
      height: scene.height,
      ...paintToKonvaFill(scene.background, scene.width, scene.height),
    },
  };
}

/** Distinct text families + their max font size, for preloading via
 *  ensureSceneFontsLoaded() before Konva draws (else text renders with fallback
 *  metrics). Pure -> unit-tested. */
export function textFontSpecs(nodes: KonvaNode[]): Array<{ family: string; size: number }> {
  const map = new Map<string, number>();
  for (const n of nodes) {
    if (n.konvaType !== 'Text') continue;
    // Preload the ASSIGNED font (stack head); the bundled fallback is a global
    // @font-face that's already present, so it needn't be force-downloaded here.
    const fam = typeof n.props.fontFamily === 'string' ? primaryFontFamily(n.props.fontFamily) : n.props.fontFamily;
    const size = typeof n.props.fontSize === 'number' ? (n.props.fontSize as number) : 16;
    if (typeof fam === 'string') map.set(fam, Math.max(map.get(fam) || 0, size));
  }
  return [...map].map(([family, size]) => ({ family, size }));
}

/** Whole scene -> ordered Konva node descriptors (background first, then z-order).
 *  `transforms` (from inline editing) override per-element geometry.
 *
 *  Konva supports only ONE shadow per node, but Figma layers can stack several
 *  (e.g. a glow = two black blurs). For >1 shadow we emit shadow-only CLONE nodes
 *  behind the element (each carrying one of the extra shadows); the real node sits
 *  on top with the SHARPEST shadow (see pickPrimaryShadow) + a clean fill. Clones are non-interactive
 *  (no elementId) so selection/transform still targets the real node. */
export interface SceneToKonvaOptions {
  /** Editor preview: prefer `override.thumbnail` over `override.highRes` for
   *  image inputs so the editor doesn't pull megabyte-scale bitmaps over the
   *  wire on every keystroke. Server-side export (renderSceneToPng) leaves
   *  this off so it gets the full-resolution asset. */
  preferThumbnail?: boolean;
  /** Per-inputKey fit override for USER-REPLACED images (deriveImageFit in
   *  ./imageFit). 'contain' letterboxes the image inside the field box instead
   *  of cover-cropping it. Absent (old stored manifests included) = the
   *  template's own fit, which is what those renders originally produced. */
  imageFit?: { [inputKey: string]: string };
}

export function sceneToKonva(scene: Scene, values: SceneValues = {}, transforms: SceneTransforms = {}, opts: SceneToKonvaOptions = {}): KonvaNode[] {
  const nodes: KonvaNode[] = [];
  const bg = backgroundNode(scene);
  if (bg) nodes.push(bg);
  for (const el of scene.elements) {
    const node = elementToKonva(el, values, transforms[el.id], opts);
    if (!node) continue;
    const shadows = el.shadows || [];
    // Skip clones for a BLENDED element: a clone redraws the full fill, which would
    // pollute the backdrop the real (blended) node composites against, and double-
    // blend. Blended elements keep just the primary shadow on the node.
    if (shadows.length > 1 && !blendModeToComposite(el.blendMode)) {
      const primary = pickPrimaryShadow(shadows);
      // Extra (softer) shadows -> clones behind the real node (drawn first = underneath).
      shadows.filter((s) => s !== primary).forEach((s, k) => {
        nodes.push({
          ...node,
          elementId: undefined,
          editable: undefined,
          inputKey: undefined,
          // A shadow clone is not the element: it must never be selectable, and
          // must never look like a linked-overlay target either.
          sourceNodeId: undefined,
          props: { ...node.props, id: `${node.props.id}__sh${k}`, ...shadowProps([s]) },
        });
      });
    }
    nodes.push(node);
  }
  return nodes;
}
